import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Allowance } from "@talent/protocol";
import { fetchClasses, type ClassList } from "../class-api.js";

/**
 * Which class the teacher is looking at.
 *
 * **One selection, held in one place, read by every teacher screen.** Everything a teacher reads is
 * scoped to a class (ADR-012), so the alternative was a `classId` prop threaded through five screens —
 * and a prop threaded that far is a prop that gets passed stale by one caller.
 *
 * The choice survives a reload, in `localStorage`. A teacher who opens the afternoon group, reads a
 * child, and refreshes must not be silently moved back to the morning one: the numbers would change
 * under them with nothing on screen saying why.
 */

const REMEMBERED = "kiddora.class";

export interface ClassContext {
  classes: ClassList;
  /** The chosen class, or null while the list is loading or when there are none. */
  current: ClassList[number] | null;
  /**
   * How much of their allowance this teacher has used, or null if it is not known yet.
   *
   * Held here because it travels with the class list and is read by two screens that are not each other's
   * parents — the classes screen offers to make one, the roster offers to add a child. Null is treated as
   * "no reason to hold anything back": the server refuses regardless, and a button hidden on a guess is
   * worse than a refusal that explains itself.
   */
  allowance: Allowance | null;
  choose: (classId: string) => void;
  /** Re-reads the list. Called after making, renaming or closing one. */
  reload: () => Promise<void>;
}

const Ctx = createContext<ClassContext | null>(null);

export function ClassProvider({
  initial,
  initialAllowance = null,
  children,
}: {
  /**
   * The classes that came back with the session, so the first roster does not wait on a second request.
   *
   * Refreshed from the server on mount all the same — a class made in another tab, or a name changed
   * there, would otherwise persist in this one until the next sign-in.
   */
  initial: ClassList;
  /** From the same session response, for the same reason. Null for anybody who is not a teacher. */
  initialAllowance?: Allowance | null;
  children: React.ReactNode;
}) {
  const [classes, setClasses] = useState<ClassList>(initial);
  const [allowance, setAllowance] = useState<Allowance | null>(initialAllowance);
  const [chosen, setChosen] = useState<string | null>(() => remembered());

  const reload = useCallback(async () => {
    // A failure leaves the list alone. This is the teacher's own session; if it has expired the screen
    // that owns the data will find out and say so, and blanking the switcher first would only remove
    // the one control that could recover.
    try {
      const listing = await fetchClasses();
      setClasses(listing.classes);
      // Re-read together with the classes, because adding a child changes one of these numbers and
      // closing a class changes the other. Two facts from one response cannot drift apart.
      setAllowance(listing.allowance);
    } catch {
      /* keep what we have */
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const choose = useCallback((classId: string) => {
    setChosen(classId);
    try {
      localStorage.setItem(REMEMBERED, classId);
    } catch {
      // Private browsing, or a full quota. Losing the memory of which class is open is a small
      // inconvenience; failing to switch would not be.
    }
  }, []);

  /**
   * The remembered class if it is still one of theirs, otherwise the first.
   *
   * Falling back matters: a class that was closed, or one belonging to a session that has since signed
   * in as somebody else, leaves an id in storage that no longer resolves — and a screen scoped to it
   * would answer 403 on every request with nothing to tap.
   */
  const current = useMemo(
    () => classes.find((c) => c.classId === chosen) ?? classes[0] ?? null,
    [chosen, classes],
  );

  const value = useMemo<ClassContext>(
    () => ({ classes, current, allowance, choose, reload }),
    [allowance, classes, choose, current, reload],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * The current class, for a screen that cannot work without one.
 *
 * Throws when used outside the provider rather than returning null, because that is a wiring mistake
 * rather than a state a teacher can reach — and a null that silently disabled a screen would be found
 * by a person instead of by the type checker.
 */
export function useClasses(): ClassContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useClasses outside ClassProvider");
  return ctx;
}

function remembered(): string | null {
  try {
    return localStorage.getItem(REMEMBERED);
  } catch {
    return null;
  }
}
